import { Link } from 'react-router-dom';
import { ArrowRight, Moon, Building2, CalendarDays, Check } from 'lucide-react';
import type { Service } from '../types';

interface ServiceCardProps {
  service: Service;
  featured?: boolean;
}

const iconMap: Record<string, React.ElementType> = {
  moon: Moon,
  building: Building2,
  calendar: CalendarDays,
};

export function ServiceCard({ service, featured = false }: ServiceCardProps) {
  const IconComponent = iconMap[service.icon] || Building2;

  return (
    <div 
      className={`group bg-white rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-luxury border border-platin-200/60 hover:border-platin-300 ${
        featured ? 'ring-2 ring-platin-300 shadow-luxury' : ''
      }`}
    >
      <div className="p-8">
        <div className="flex items-start justify-between mb-6">
          <div 
            className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-300 ${
              featured 
                ? 'bg-brand-950 text-platin-200' 
                : 'bg-platin-100 text-brand-600 group-hover:bg-brand-950 group-hover:text-platin-200'
            }`}
            aria-hidden="true"
          >
            <IconComponent className="w-8 h-8" />
          </div>
          {featured && (
            <span className="px-4 py-1.5 bg-platin-200 text-brand-950 text-xs font-semibold rounded-full">
              24/7 Verfügbar
            </span>
          )}
        </div>

        <h3 className="text-2xl font-bold text-brand-950 mb-4 group-hover:text-brand-700 transition-colors">
          {service.title}
        </h3>
        
        <p className="text-gray-600 mb-8 leading-relaxed">
          {service.description}
        </p>

        <ul className="space-y-3 mb-8" role="list">
          {service.features.slice(0, 4).map((feature, index) => (
            <li key={index} className="flex items-start gap-3 text-sm text-gray-600">
              <Check className="w-5 h-5 text-platin-500 shrink-0 mt-0.5" aria-hidden="true" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <Link
          to={`/leistungen#${service.id}`}
          className="inline-flex items-center gap-2 text-brand-950 font-semibold hover:text-brand-700 transition-colors group/link"
        >
          Mehr erfahren
          <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}
